// WHAT A SCREEN IS SENT WITH (29/10/2026). Screen only (and Verify from
// documents) send Sumsub a date of birth and a nationality when the officer
// gives them. Before, a date not written as YYYY-MM-DD or a nationality not
// three letters was dropped without a word, and the person was screened on the
// name only. Now one set of rules for the form and the server:
//   a blank is accepted (sent without it, and the case note says so);
//   anything else must read right, or it is refused with a plain message;
//   a nationality may be typed as a country or nationality word ("United
//   Kingdom", "British") and is sent as its three letter code.
// The form is prefilled from the case wherever the case holds them, so the
// officer confirms rather than types.
import { toAlpha3, ISO3 } from './country-codes.js';

export const DOB_FORMAT_ERROR = 'Date of birth must be in the form 1975-03-12';

export function parseDob(input, today = new Date()) {
  const raw = String(input == null ? '' : input).trim();
  if (!raw) return { ok: true, value: null };
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return { ok: false, error: DOB_FORMAT_ERROR + ` (you typed "${raw}").` };
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return { ok: false, error: `${raw} is not a real date. ${DOB_FORMAT_ERROR}.` };
  if (y < 1900) return { ok: false, error: `Date of birth ${raw} is before 1900. Check the year.` };
  if (dt.getTime() > today.getTime()) return { ok: false, error: `Date of birth ${raw} is in the future. Check the date.` };
  return { ok: true, value: raw };
}

export function parseNationality(input) {
  const raw = String(input == null ? '' : input).trim();
  if (!raw) return { ok: true, value: null };
  const code = toAlpha3(raw);
  if (!code) return { ok: false, error: `Nationality "${raw}" is not a country we recognise. Type the country (for example United Kingdom) or its three letter code (GBR).` };
  return { ok: true, value: code, name: ISO3[code], converted: code !== raw.toUpperCase() };
}

// A country the documents need (the passport's issuer, the address's country):
// required, and read the same way as a nationality (29/10/2026, owner: "United
// Kingdom" works in all three). `what` names the field in the message.
export function parseCountry(input, what) {
  const raw = String(input == null ? '' : input).trim();
  if (!raw) return { ok: false, value: null, error: `Give ${what}: the country (for example United Kingdom) or its three letter code (GBR).` };
  const code = toAlpha3(raw);
  if (!code) return { ok: false, value: null, error: `"${raw}" is not a country we recognise for ${what}. Type the country (for example United Kingdom) or its three letter code (GBR).` };
  return { ok: true, value: code, name: ISO3[code] };
}
export const PASSPORT_COUNTRY = 'the country that issued the passport';
export const ADDRESS_COUNTRY = 'the country of the address';

// ── Prefill from the case ──────────────────────────────────────────────────
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const pad = (n) => String(n).padStart(2, '0');
// A date as the case holds it, in the form the screen takes when it can be read
// without guessing (an ISO date or timestamp, "12 March 1975", "March 12, 1975").
// A date like 03/04/1975 is left as it is: the officer is shown it and the
// format message, never a guess between the 3rd of April and the 4th of March.
export function dobFromCase(v) {
  const raw = String(v == null ? '' : v).trim();
  if (!raw) return '';
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})(?:[T ].*)?$/);
  if (iso) return iso[1];
  const dmy = raw.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)\.?,?\s+(\d{4})$/);
  const mdy = raw.match(/^([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/);
  const month = (w) => { const i = MONTHS.findIndex(m => m === w.toLowerCase() || m.slice(0, 3) === w.toLowerCase()); return i < 0 ? null : i + 1; };
  if (dmy && month(dmy[2])) return `${dmy[3]}-${pad(month(dmy[2]))}-${pad(dmy[1])}`;
  if (mdy && month(mdy[1])) return `${mdy[3]}-${pad(month(mdy[1]))}-${pad(mdy[2])}`;
  return raw;
}

const nameOf = (p) => (p && typeof p === 'object') ? (p.name || p.fullName || '') : String(p || '');
const firstNat = (v) => Array.isArray(v) ? v.find(x => String(x || '').trim()) : v;
// The person's date of birth and nationality from the case: the individual
// client's own record, then the person's entries on the board (directors,
// owners, signatories, shareholders, the identity list). The first entry that
// holds each wins. Returns the values as the form shows them, and where each
// came from.
export function caseScreeningDetails(report, personName, match = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase()) {
  const r = (report && typeof report === 'object') ? report : {};
  const out = { dob: '', nationality: '', dobFrom: null, nationalityFrom: null };
  if (!personName) return out;
  const entries = [];
  if (String(r.clientKind || '').toLowerCase() === 'individual' && r.clientPerson && typeof r.clientPerson === 'object') {
    entries.push(['the client record', { ...r.clientPerson, name: r.clientPerson.fullName || r.clientName }]);
  }
  for (const [list, label] of [['directors', 'the directors on the file'], ['ubos', 'the owners on the file'], ['authorisedSignatories', 'the signatories on the file'], ['shareholders', 'the shareholders on the file'], ['sumsubRequired', 'the identity list']]) {
    for (const e of (Array.isArray(r[list]) ? r[list] : [])) if (e && typeof e === 'object') entries.push([label, e]);
  }
  for (const [from, e] of entries) {
    const n = nameOf(e);
    if (!n || !match(n, personName)) continue;
    const dob = e.dateOfBirth || e.dob || e.birthDate;
    const nat = firstNat(e.nationality) || firstNat(e.nationalities);
    if (!out.dob && dob && String(dob).trim()) { out.dob = dobFromCase(dob); out.dobFrom = from; }
    if (!out.nationality && nat && String(nat).trim() && !/^[.…]+$/.test(String(nat).trim())) { out.nationality = toAlpha3(nat) || String(nat).trim(); out.nationalityFrom = from; }
  }
  return out;
}

// What the case note says was sent (so the officer can see a screen on name only).
export function sentWithWords({ personName, dob, nationality }) {
  const parts = [`the name ${personName}`];
  parts.push(dob ? `date of birth ${dob}` : 'no date of birth');
  parts.push(nationality ? `nationality ${nationality}` : 'no nationality');
  const list = parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
  return `Sent to Sumsub with ${list}.` + (!dob && !nationality ? ' Screening ran on the name only, so a match on another person of the same name cannot be ruled out from these details.' : '');
}
